import {
    MouseEvent, UIEvent, useCallback, useMemo, useRef,
} from 'react';
import { Calendar } from 'data/firestore/agent/calendar/types';
import { Calendarevent } from 'data/firestore/agent/calendarevent/types';
import { CalendarEventClick } from 'types';
import { DIVIDER, PURPLE_500 } from '../../constants';
import CalendarItem from '../CalendarDayView/calendars/calendar';
import ClockLine from '../CalendarDayView/calendars/clockLine';
import getMinAvailableStartAt from '../CalendarDayView/helpers/getMinAvailableStartAt';
import useScrollToMinAvailableTime from '../CalendarDayView/hooks/useScrollToMinAvailableTime';
import useScrollToDayColumn, { getDayColumnId } from './hooks/useScrollToDayColumn';
import {
    Container,
    Content,
    Grid,
    GridLine,
    Scroll,
    StyledCalendars,
} from '../CalendarDayView/calendars/styled';

export interface WeekDayColumn {
    day: Date;
    events: Calendarevent[];
    availability: Calendar['availability'];
    isCollapsed: boolean;
}

interface DaysProps {
    days: WeekDayColumn[];
    calendars: Calendar[];
    selectedEvent?: string | null;
    todayIndex?: number;
    onScroll: (e: UIEvent<HTMLDivElement>) => void;
    onEventClick?: CalendarEventClick;
    onAvailableSpaceClick: (day: Date, e: MouseEvent<HTMLDivElement>) => void;
    hideClockLine?: boolean;
    timeZone: string;
}

const Days = ({
    days,
    calendars,
    selectedEvent,
    todayIndex = -1,
    onScroll = () => {},
    onEventClick = () => {},
    onAvailableSpaceClick = () => {},
    hideClockLine = false,
    timeZone,
}: DaysProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const colorsByCalendarId = useMemo(
        () => calendars.reduce<Record<string, string>>((acc, calendar) => {
            acc[calendar.id] = calendar.color;
            return acc;
        }, {}),
        [calendars],
    );

    const namesByCalendarId = useMemo(
        () => calendars.reduce<Record<string, string>>((acc, calendar) => {
            acc[calendar.id] = calendar.name;
            return acc;
        }, {}),
        [calendars],
    );

    const handleAvailableSpaceClick = useCallback(
        (day: Date) => (
            e: MouseEvent<HTMLDivElement>,
        ) => onAvailableSpaceClick(day, e),
        [onAvailableSpaceClick],
    );

    const dayColumns = useMemo(
        () => days.map(({
            day, events, availability, isCollapsed,
        }, index) => (
            <CalendarItem
                key={day.toISOString()}
                id={getDayColumnId(index)}
                color={calendars[0]?.color || PURPLE_500}
                getEventColor={(event) => (
                    colorsByCalendarId[event.calendarId] || calendars[0]?.color || PURPLE_500
                )}
                events={events}
                selectedEvent={selectedEvent}
                availability={availability}
                isCollapsed={isCollapsed}
                calendarNames={namesByCalendarId}
                onEventClick={onEventClick}
                onAvailableSpaceClick={handleAvailableSpaceClick(day)}
                timeZone={timeZone}
            />
        )),
        [
            calendars,
            colorsByCalendarId,
            namesByCalendarId,
            days,
            handleAvailableSpaceClick,
            onEventClick,
            selectedEvent,
            timeZone,
        ],
    );

    const gridLines = useMemo(
        () => Array.from(
            Array(48),
            (_value, index) => {
                const color = (
                    index % 2 === 0
                        ? DIVIDER
                        : `${DIVIDER}72`
                );
                return <GridLine key={index} color={color} />;
            },
        ),
        [],
    );

    const availability = useMemo(
        () => days.flatMap((column) => column.availability),
        [days],
    );

    // The horizontal scroll has to be recomputed both when the displayed week changes
    // and when a different set of days collapses, since that changes column widths.
    const layoutKey = useMemo(
        () => [
            days[0]?.day.toISOString(),
            days.map(({ isCollapsed }) => Number(isCollapsed)).join(''),
        ].join('|'),
        [days],
    );

    useScrollToMinAvailableTime(scrollRef, getMinAvailableStartAt(availability));
    useScrollToDayColumn(scrollRef, todayIndex, layoutKey);

    return (
        <Scroll
            ref={scrollRef}
            onScroll={onScroll}
        >
            <Container>
                <Content>
                    <Grid>
                        {gridLines}
                    </Grid>
                    <ClockLine visible={!hideClockLine} />
                    <StyledCalendars>
                        <div />
                        {dayColumns}
                    </StyledCalendars>
                </Content>
            </Container>
        </Scroll>
    );
};

export default Days;
